export enum OrgPermissions {
	MEMBER = 0x0,
	ADMINISTRATOR = 0x1,
	MANAGE_ORG = 0x2,
	MANAGE_ORG_MEMBERS = 0x4,
	CREATE_TEAMS = 0x8,
	ARCHIVE_TEAMS = 0x10,
	MANAGE_ROLES = 0x20,
	VIEW_AUDIT_LOG = 0x40,
	MANAGE_EMOJIS = 0x80
}

export enum PublicSuggestionPermissions {
	VIEW = 0x1,
	SUBMIT = 0x2,
	VOTE = 0x4,
	VIEW_LINKED_STATUS = 0x8,
	COMMENT = 0x10,
	EDIT_OWN = 0x20
}

export enum TeamPermissions {
	MEMBER = 0x0,
	ADMINISTRATOR = 0x1,
	MANAGE_TEAM = 0x2,
	MANAGE_TEAM_MEMBERS = 0x4,
	ARCHIVE_TEAM = 0x8,
	CREATE_ISSUES = 0x10,
	EDIT_ISSUES = 0x20,
	DELETE_ISSUES = 0x40,
	COMMENT_ISSUES = 0x80,
	CREATE_QUEUE_ISSUES = 0x100,
	MANAGE_LABELS = 0x200,
	MANAGE_ROLES = 0x400,
	RESOLVE_COMMENTS = 0x800,
	VIEW_KNOWLEDGE_BASE = 0x1000,
	EDIT_KNOWLEDGE_BASE = 0x2000,
	VIEW_AUDIT_LOG = 0x4000,
	COMMENT_KNOWLEDGE_BASE = 0x8000,
	VIEW_FORMS = 0x10000,
	VIEW_FORM_SUBMISSIONS = 0x20000,
	EDIT_FORMS = 0x40000
}

export const permissionsDescriptions = {
	MEMBER: "Given to all members, no permissions attached",
	ADMINISTRATOR: "Administrator",
	MANAGE_ORG: "Manage organization settings",
	MANAGE_EMOJIS: "Manage custom emojis",
	MANAGE_ORG_MEMBERS: "Manage organization members",
	CREATE_TEAMS: "Create teams",
	ARCHIVE_TEAMS: "Archive teams",
	CREATE_ISSUES: "Create issues",
	EDIT_ISSUES: "Edit issues",
	DELETE_ISSUES: "Delete issues",
	COMMENT_ISSUES: "Comment on issues",
	CREATE_QUEUE_ISSUES: "Create queue issues",
	MANAGE_TEAM: "Manage team settings",
	MANAGE_TEAM_MEMBERS: "Manage team members",
	ARCHIVE_TEAM: "Archive team",
	MANAGE_LABELS: "Manage labels",
	MANAGE_ROLES: "Manage roles and permissions",
	RESOLVE_COMMENTS: "Resolve comments",
	VIEW_AUDIT_LOG: "View audit log",
	VIEW_KNOWLEDGE_BASE: "View knowledge base",
	EDIT_KNOWLEDGE_BASE: "Edit knowledge base",
	COMMENT_KNOWLEDGE_BASE: "Comment on knowledge base documents",
	VIEW_FORMS: "View forms",
	VIEW_FORM_SUBMISSIONS: "View form submissions",
	EDIT_FORMS: "Edit forms"
} as const satisfies Record<
	keyof typeof OrgPermissions | keyof typeof TeamPermissions,
	string
>

export const publicSuggestionPermissionDescriptions = {
	VIEW: "View suggestions",
	SUBMIT: "Submit suggestions",
	VOTE: "Upvote suggestions",
	VIEW_LINKED_STATUS: "View linked issue status",
	COMMENT: "Comment on suggestions",
	EDIT_OWN: "Edit own suggestions"
} as const satisfies Record<keyof typeof PublicSuggestionPermissions, string>

export const hasPublicSuggestionPermission = (
	permissionsUserHas: number,
	permissionsNeeded: number
) => (permissionsUserHas & permissionsNeeded) === permissionsNeeded

export const defaultPublicSuggestionPermissions =
	PublicSuggestionPermissions.VIEW |
	PublicSuggestionPermissions.SUBMIT |
	PublicSuggestionPermissions.VOTE |
	PublicSuggestionPermissions.VIEW_LINKED_STATUS

export const hasOrgPermission = (
	permissionsUserHas: number,
	permissionsNeeded: number,
	adminCanBypass = true
) => {
	if (permissionsNeeded === OrgPermissions.MEMBER) {
		return true
	}
	if (
		adminCanBypass &&
		(permissionsUserHas & OrgPermissions.ADMINISTRATOR) ===
			OrgPermissions.ADMINISTRATOR
	) {
		return true
	}

	return (permissionsUserHas & permissionsNeeded) === permissionsNeeded
}

export const hasTeamPermission = (
	permissionsUserHas: number,
	permissionsNeeded: number,
	adminCanBypass = true
) => {
	if (permissionsNeeded === TeamPermissions.MEMBER) {
		return true
	}
	if (
		adminCanBypass &&
		(permissionsUserHas & TeamPermissions.ADMINISTRATOR) ===
			TeamPermissions.ADMINISTRATOR
	) {
		return true
	}

	return (permissionsUserHas & permissionsNeeded) === permissionsNeeded
}

export const maxOrgPermissions = Object.values(OrgPermissions)
	.filter((x) => !Number.isNaN(Number(x)))
	.reduce((acc, permission) => acc | Number(permission), 0)

export const maxTeamPermissions = Object.values(TeamPermissions)
	.filter((x) => !Number.isNaN(Number(x)))
	.reduce((acc, permission) => acc | Number(permission), 0)

export const orgPermissionsFromInteger = (
	permissions: number
): OrgPermissions[] => {
	return Object.values(OrgPermissions)
		.filter((x) => !Number.isNaN(Number(x)))
		.map((x) => Number(x))
		.filter((permission) => (permissions & permission) === permission)
}

export const teamPermissionsFromInteger = (
	permissions: number
): TeamPermissions[] => {
	return Object.values(TeamPermissions)
		.filter((x) => !Number.isNaN(Number(x)))
		.map((x) => Number(x))
		.filter((permission) => (permissions & permission) === permission)
}

export const canAccessOrgSettings = (permissions: number) =>
	[
		OrgPermissions.MANAGE_ORG,
		OrgPermissions.MANAGE_ORG_MEMBERS,
		OrgPermissions.MANAGE_ROLES,
		OrgPermissions.VIEW_AUDIT_LOG,
		OrgPermissions.MANAGE_EMOJIS
	].some((permission) => hasOrgPermission(permissions, permission))
